// src/components/BookingDetailContainer.js
import { useState, useEffect, useRef, useCallback } from 'react';
import {
    getTransaction,
    getConversationByTransactionId,
    getConversationMessages,
    sendMessage,
    getUserBasicInfo,
    updateTransactionStatus,
    getListingBasicInfo,
} from '../services/api';
import { useAuth } from '../context/AuthContext';

const BookingDetailContainer = ({ transactionId, children }) => {
    const { currentUser, loading: authLoading } = useAuth();
    const [transaction, setTransaction] = useState(null);
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newMessage, setNewMessage] = useState('');
    const [otherUserInfo, setOtherUserInfo] = useState(null);
    const [listingData, setListingData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const lastActivityTime = useRef(Date.now());
    const isActive = useRef(true);
    const pollingInterval = useRef(null);

    // Update user activity timestamp
    const updateLastActivity = useCallback(() => {
        lastActivityTime.current = Date.now();
        isActive.current = true;
    }, []);

    // Fetch conversation messages
    const fetchMessages = useCallback(
        async (convId, isPolling = false) => {
            try {
                const messagesResponse = await getConversationMessages(convId);
                const newMessages = messagesResponse.data;
                if (isPolling && newMessages.length > messages.length) {
                    setSnackbar({ open: true, message: 'New message received', severity: 'info' });
                }
                setMessages(newMessages);
            } catch (err) {
                console.error('Error fetching messages:', err);
            }
        },
        [messages.length]
    );

    // Fetch transaction, listing, user and conversation data
    const fetchTransactionAndConversation = useCallback(async () => {
        try {
            setLoading(true);
            const transactionResponse = await getTransaction(transactionId);
            setTransaction(transactionResponse.data);

            const listingResp = await getListingBasicInfo(transactionResponse.data.listing_id);
            setListingData(listingResp.data);

            const otherUserId =
                currentUser && transactionResponse.data
                    ? currentUser.id === transactionResponse.data.lender_id
                        ? transactionResponse.data.borrower_id
                        : transactionResponse.data.lender_id
                    : null;

            if (otherUserId) {
                const userResp = await getUserBasicInfo(otherUserId);
                setOtherUserInfo(userResp.data);
            }

            const conversationResponse = await getConversationByTransactionId(transactionId);
            if (conversationResponse.data && conversationResponse.data.length > 0) {
                const conv = conversationResponse.data[0];
                setConversation(conv);
                await fetchMessages(conv.id);
            }
        } catch (err) {
            console.error('Error fetching transaction and conversation:', err);
            setSnackbar({ open: true, message: 'Error fetching transaction data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [transactionId, currentUser, fetchMessages]);

    // On mount
    useEffect(() => {
        if (!authLoading && currentUser) {
            fetchTransactionAndConversation();
        }
        window.addEventListener('mousemove', updateLastActivity);
        window.addEventListener('keydown', updateLastActivity);
        window.addEventListener('click', updateLastActivity);
        window.addEventListener('scroll', updateLastActivity);

        return () => {
            window.removeEventListener('mousemove', updateLastActivity);
            window.removeEventListener('keydown', updateLastActivity);
            window.removeEventListener('click', updateLastActivity);
            window.removeEventListener('scroll', updateLastActivity);
            if (pollingInterval.current) clearInterval(pollingInterval.current);
        };
    }, [authLoading, currentUser, transactionId, updateLastActivity, fetchTransactionAndConversation]);

    // Start polling messages if a conversation exists
    useEffect(() => {
        if (!conversation) return;
        const startPolling = () => {
            if (pollingInterval.current) clearInterval(pollingInterval.current);
            pollingInterval.current = setInterval(() => {
                if (isActive.current && Date.now() - lastActivityTime.current <= 10000) {
                    fetchMessages(conversation.id, true);
                } else {
                    isActive.current = false;
                }
            }, 10000);
        };
        startPolling();
        return () => {
            if (pollingInterval.current) clearInterval(pollingInterval.current);
        };
    }, [conversation, fetchMessages]);

    // Send a new message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || !conversation) return;
        try {
            await sendMessage({
                conversation_id: conversation.id,
                content: newMessage,
                borrower_id: conversation.borrower_id,
                lender_id: conversation.lender_id,
                sender_id: currentUser.id,
            });
            setNewMessage('');
            await fetchMessages(conversation.id);
            updateLastActivity();
        } catch (err) {
            console.error('Error sending message:', err);
            setSnackbar({ open: true, message: 'Error sending message', severity: 'error' });
        }
    };

    // Update transaction status
    const handleUpdateStatus = async (status) => {
        try {
            const steps = [
                { label: 'Pending', status: 'pending' },
                { label: 'Accepted', status: 'accepted' },
                { label: 'Paid', status: 'paid' },
                { label: 'In Progress', status: 'in_progress' },
                { label: 'Completed', status: 'completed' },
            ];
            const matchingStep = steps.find((step) => step.status === status);
            const statusLabel = matchingStep ? matchingStep.label : status;
            await updateTransactionStatus(transactionId, { status });
            setSnackbar({ open: true, message: `Transaction ${statusLabel}`, severity: 'success' });
            await fetchTransactionAndConversation();
        } catch (err) {
            console.error(`Error updating transaction status to ${status}:`, err);
            setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
        }
    };

    // Build steps for the progress bar
    const getStepStatus = () => {
        const steps = [
            { label: 'Pending', status: 'pending' },
            { label: 'Accepted', status: 'accepted' },
            { label: 'Paid', status: 'paid' },
            { label: 'In Progress', status: 'in_progress' },
            { label: 'Completed', status: 'completed' },
        ];
        const currentIndex = steps.findIndex((step) => step.status === transaction?.status);
        return steps.map((step, index) => ({
            label: step.label,
            completed: index <= currentIndex,
            active: index === currentIndex,
        }));
    };

    return children({
        transaction,
        conversation,
        messages,
        loading,
        newMessage,
        setNewMessage,
        handleSendMessage,
        handleUpdateStatus,
        getStepStatus,
        otherUserInfo,
        listingData,
        snackbar,
        setSnackbar,
    });
};

export default BookingDetailContainer;
